import { CommonModule } from '@angular/common';
import { Component, ElementRef, inject, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DashboardDialogFramePart, DialogAction, DialogContext } from '@eforall/dashboard';
import { KENDO_LAYOUT } from "@progress/kendo-angular-layout";
import { MonacoEditorModule } from 'ngx-monaco-editor-v2';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import embed from 'vega-embed';
import { VegaService } from '../vega.service';


@Component({
	selector: 'edit-chart-dialog',
	standalone: true,
	imports: [
		CommonModule, FormsModule, MonacoEditorModule, DashboardDialogFramePart, KENDO_LAYOUT
	],
	templateUrl: './edit-chart.dialog.html',
	styleUrl: './edit-chart.dialog.scss',
})

export class EditChartDialog implements OnInit, OnDestroy {

	@ViewChild('chartContainer', { static: true }) chartContainer!: ElementRef;

	public dialogRef = inject(MatDialogRef<EditChartDialog>);
	public dialogContext: DialogContext<{}> = inject(MAT_DIALOG_DATA);
	public vegaService = inject(VegaService);


	public actions: DialogAction[] = [
		{
			id: 'close',
			cssClass: signal(undefined),
			enabled: signal(true),
			visible: signal(true),
			text: signal('Close'),
			linkType: 'no-action',
			willCloseDialog: true,
		},
	];


	public specString: string = JSON.stringify({
		"$schema": "https://vega.github.io/schema/vega-lite/v5.json",
		"description": "A simple bar chart with embedded data.",
		"data": {
			"values": [
				{ "category": "A", "amount": 28 }, { "category": "B", "amount": 55 },
				{ "category": "C", "amount": 43 }, { "category": "D", "amount": 91 }
			]
		},
		"mark": "bar",
		"encoding": {
			"x": { "field": "category", "type": "nominal" },
			"y": { "field": "amount", "type": "quantitative" }
		}
	}, null, 2);


	public editorOptions = {
		theme: 'vs-light',
		language: 'json',
		automaticLayout: true,
		scrollBeyondLastLine: false,
		minimap: { enabled: false },
		scrollbar: {
			vertical: 'visible',
			horizontal: 'visible',
			useShadows: false, // Optional: removes the shadow when scrolling
			verticalHasArrows: false,
			horizontalHasArrows: false
		}
	};


	private updateSubject = new Subject<string>();
	private updateSubscription!: Subscription;


	ngOnInit(): void {
		// 1. Initial render
		this.renderVega(this.specString);

		// 2. Setup debounced listener for changes
		this.updateSubscription = this.updateSubject.pipe(
			debounceTime(500),      // Wait 500ms after user stops typing
			distinctUntilChanged()  // Only update if the text actually changed
		).subscribe(newSpec => {
			this.renderVega(newSpec);
		});
	}


	// Triggered by (ngModelChange) in the template
	onSpecChange(value: string) {
		this.updateSubject.next(value);
	}


	private async renderVega(spec: string) {
		try {
			const parsed = JSON.parse(spec);

			// embed(element, spec, options)
			// vega-embed automatically detects if it is Vega or Vega-Lite based on the $schema
			await embed(this.chartContainer.nativeElement, parsed, { actions: false, renderer: 'canvas' });
		} catch (e) {
			// We do nothing on error. Monaco handles the visual error reporting
			// via the JSON schema validation we set up in main.ts.
			console.error(`JSON Parse Error`, e);
		}
	}


	ngOnDestroy(): void {
		if (this.updateSubscription) {
			this.updateSubscription.unsubscribe();
		}
	}
}
